import { useEffect, useState } from 'react';
import { useFireTrackingEvent } from '../../../../hooks/tracking/useTrackingEvents';
import deepEqual from 'fast-deep-equal';

const hasFilters = (filters: IFilterValues) =>
  Object.keys(filters).find((values) => !!Object.keys(values).length);

const isRangeFilter = (filterValue: FilterValue) =>
  filterValue.min !== undefined && filterValue.max !== undefined;

const isBoolFilter = (filterValue: FilterValue) => filterValue.selected !== undefined;

const getFilterDiff = (prev: FilterValue | undefined, next: FilterValue) =>
  Object.keys(next).filter((key) => !prev?.[key] || prev[key] !== next[key]);

const getFiltersEvent = (prev: IFilterValues, next: IFilterValues) => {
  for (const nextKey in next) {
    if (!Object.prototype.hasOwnProperty.call(next, nextKey)) {
      continue;
    }

    const diff = getFilterDiff(prev[nextKey], next[nextKey]);

    if (!diff.length) {
      continue;
    }

    if (isRangeFilter(next[nextKey])) {
      return {
        event: `product_list_filter`,
        category: 'range',
        label: nextKey,
        value: `${next[nextKey].min}-${next[nextKey].max}`,
      };
    } else if (isBoolFilter(next[nextKey])) {
      return {
        event: `product_list_filter`,
        category: 'bool',
        label: nextKey,
        value: `${next[nextKey].selected}`,
      };
    } else {
      return {
        event: `product_list_filter`,
        category: 'multi',
        label: nextKey,
        value: `${diff[0]}`,
      };
    }
  }

  return null;
};

const getLeadtimeEvent = (prev: IFilterValues, next: IFilterValues) => {
  if (!Object.keys(next).length) {
    return null;
  }

  if (!Object.keys(prev).length) {
    return {
      event: 'leadtime_filter',
      value: '',
    };
  }

  return {
    event: 'leadtime_filter',
    value: next.stockStatus ? '' : Object.keys(next)?.[0] || '',
  };
};

const useTrackFilters = (filters: IFilters) => {
  const [prevFilters, setPrevFilters] = useState(filters);
  const track = useFireTrackingEvent();

  useEffect(() => {
    if (hasFilters(filters.regular)) {
      const event = getFiltersEvent(prevFilters.regular, filters.regular);

      if (event) {
        track(event);
      }
    }

    if (!deepEqual(filters.leadtime, prevFilters.leadtime) && hasFilters(filters.leadtime)) {
      const event = getLeadtimeEvent(prevFilters.leadtime, filters.leadtime);

      if (event) {
        track(event);
      }
    }

    setPrevFilters(filters);
  }, [filters, prevFilters]);
};

export default useTrackFilters;
